import { RefObject, useEffect, useRef, useState } from 'react';

export interface IAudioPlayback {
    audioRef: RefObject<HTMLAudioElement>;
    isPlaying: boolean;
    currentTime: number;
    duration: number;
    progress: number;
    togglePlay: () => void;
    seekToFraction: (fraction: number) => void;
    pause: () => void;
}

export const useAudioPlayback = ({ src }: { src: string }): IAudioPlayback => {
    const audioRef = useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [progress, setProgress] = useState(0);

    // The hook assumes the caller always renders <audio ref={audioRef} /> synchronously
    // on first render, which both consumers (OfflineRecordingListItem, legacy RecordingPlayer)
    // do today. If a future caller wraps it in a Suspense boundary or feature flag, this
    // mount-time attach won't find the element and silently no-op — restructure to a
    // callback ref at that point.
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) {
            return;
        }

        const handleLoadedMetadata = () => {
            setDuration(audio.duration || 0);
        };

        const handleTimeUpdate = () => {
            const current = audio.currentTime || 0;
            setCurrentTime(current);
            const dur = audio.duration || 0;
            setProgress(dur > 0 ? current / dur : 0);
        };

        const handleEnded = () => {
            // Reset the element's playhead too so the next play() restarts from the beginning
            // instead of replaying-from-end (which fires 'ended' again immediately).
            audio.currentTime = 0;
            setCurrentTime(0);
            setProgress(0);
        };

        const handleCanPlay = () => {
            if (audio.duration && isFinite(audio.duration)) {
                setDuration(audio.duration);
            }
        };

        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);

        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('canplay', handleCanPlay);
        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handlePause);

        return () => {
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('canplay', handleCanPlay);
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('pause', handlePause);
        };
    }, []);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) {
            return;
        }
        audio.src = src;
    }, [src]);

    const togglePlay = () => {
        const audio = audioRef.current;
        if (!audio) {
            return;
        }
        if (audio.paused) {
            // play() may reject (autoplay policy, src not ready, navigation). The 'play'
            // event won't fire on rejection, so isPlaying stays false via the listener —
            // no need to handle the rejection here.
            audio.play().catch(() => {});
        } else {
            audio.pause();
        }
    };

    const seekToFraction = (fraction: number) => {
        const audio = audioRef.current;
        if (!audio || !audio.duration || !isFinite(audio.duration)) {
            return;
        }
        const clamped = Math.max(0, Math.min(1, fraction));
        audio.currentTime = clamped * audio.duration;
    };

    const pause = () => {
        const audio = audioRef.current;
        if (!audio) {
            return;
        }
        audio.pause();
    };

    return {
        audioRef,
        isPlaying,
        currentTime,
        duration,
        progress,
        togglePlay,
        seekToFraction,
        pause
    };
};
